import { Alert, Divider, Modal, Select, Typography } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoInspection,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

import {
  InspectionHeatmapPicker,
  SelectedBinValue,
} from './InspectionHeatmapPicker'
import { getBestThicknessCdf } from './useComponentInspectionsData'

const { Text, Title } = Typography

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const InspectionSelectRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`

const InspectionSelectLabel = styled(Text)`
  min-width: 6.25rem;
`

const HeatmapsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`

const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.5rem;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
`

export interface CorrosionRatePickerModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (corrosionRate: GeckoMeasurement) => void
  /** All inspections for the component, sorted newest first */
  inspections: ReadonlyArray<GeckoInspection>
  /** Map of inspection ID to thickness CDFs for that inspection */
  inspectionCdfs: Map<string, GeckoComponentDataFrame[]>
  /** Loading state */
  isLoading?: boolean
  /** Enable boundary selection mode (only allow perimeter selection) */
  boundarySelectionEnabled?: boolean
  /** Expansion distance for boundary selection (default: 2cm) */
  boundaryExpansion?: GeckoMeasurement
}

/**
 * Modal for selecting short-term corrosion rate by comparing thickness
 * values from two different inspections.
 *
 * The user selects two inspections (A = older, B = newer), then clicks on
 * the heatmaps to pick specific bin values. The corrosion rate is calculated
 * as (thickness_A - thickness_B) / years_between_inspections.
 */
export const CorrosionRatePickerModal: FC<CorrosionRatePickerModalProps> = ({
  open,
  onClose,
  onConfirm,
  inspections,
  inspectionCdfs,
  isLoading = false,
  boundarySelectionEnabled = false,
  boundaryExpansion = GeckoMeasurement.CreateFromValue(
    20,
    GeckoMeasurementUnit.Millimeters
  ),
}) => {
  const { indication, component } = useIndicationData()

  // State for selected inspections
  const [inspectionAId, setInspectionAId] = useState<string | null>(null)
  const [inspectionBId, setInspectionBId] = useState<string | null>(null)

  // State for selected bin values
  const [selectedBinA, setSelectedBinA] = useState<SelectedBinValue | null>(
    null
  )
  const [selectedBinB, setSelectedBinB] = useState<SelectedBinValue | null>(
    null
  )

  // Get the actual inspection objects
  const inspectionA = useMemo(
    () => inspections.find((i) => i.id === inspectionAId),
    [inspections, inspectionAId]
  )
  const inspectionB = useMemo(
    () => inspections.find((i) => i.id === inspectionBId),
    [inspections, inspectionBId]
  )

  // Get CDFs for the selected inspections
  const cdfA = useMemo(
    () =>
      inspectionAId
        ? getBestThicknessCdf(inspectionAId, inspectionCdfs)
        : undefined,
    [inspectionAId, inspectionCdfs]
  )
  const cdfB = useMemo(
    () =>
      inspectionBId
        ? getBestThicknessCdf(inspectionBId, inspectionCdfs)
        : undefined,
    [inspectionBId, inspectionCdfs]
  )

  const componentId = useMemo(() => component?.id, [component])

  // Get the indication region as a GeckoComponentRect
  const indicationRegion: GeckoComponentRect | undefined = useMemo(() => {
    if (!indication?.selection || !componentId) return undefined
    return GeckoComponentRect.CreateFromRanges(
      componentId,
      indication.selection.x,
      indication.selection.y
    )
  }, [indication?.selection, componentId])

  // Calculate expanded region for boundary selection mode
  const expandedRegion: GeckoComponentRect | undefined = useMemo(() => {
    if (!boundarySelectionEnabled || !indicationRegion || !boundaryExpansion) {
      return undefined
    }

    if (!componentId) {
      return undefined
    }

    // Convert expansion to the same units as the region
    const expansionX = boundaryExpansion.convertTo(indicationRegion.x.unit)
    const expansionY = boundaryExpansion.convertTo(indicationRegion.y.unit)

    // Expand on all sides
    // X axis: Allow wrapping (don't clip) - pipes wrap on X
    // Y axis: Clip to positive values - pipes don't wrap on Y
    const newXStart = indicationRegion.start.x.num - expansionX.num
    const newXEnd = indicationRegion.end.x.num + expansionX.num
    const newYStart = Math.max(0, indicationRegion.start.y.num - expansionY.num)
    const newYEnd = indicationRegion.end.y.num + expansionY.num

    // Create expanded rect using CreateFromRanges
    const expandedXRange = GeckoMeasurementRange.CreateFromValues(
      newXStart,
      newXEnd,
      indicationRegion.x.unit
    )
    const expandedYRange = GeckoMeasurementRange.CreateFromValues(
      newYStart,
      newYEnd,
      indicationRegion.y.unit
    )

    return GeckoComponentRect.CreateFromRanges(
      componentId,
      expandedXRange,
      expandedYRange
    )
  }, [
    boundarySelectionEnabled,
    indicationRegion,
    boundaryExpansion,
    componentId,
  ])

  // Auto-select first two inspections when data loads
  useEffect(() => {
    if (inspections.length >= 2 && !inspectionAId && !inspectionBId) {
      // Select oldest as A, newest as B (list is sorted newest first)
      setInspectionAId(inspections[inspections.length - 1].id)
      setInspectionBId(inspections[0].id)
    } else if (inspections.length === 1 && !inspectionAId) {
      setInspectionAId(inspections[0].id)
    }
  }, [inspections, inspectionAId, inspectionBId])

  // Reset bin selections when inspection changes
  useEffect(() => {
    setSelectedBinA(null)
  }, [inspectionAId])

  useEffect(() => {
    setSelectedBinB(null)
  }, [inspectionBId])

  // Format inspection for display
  const formatInspection = useCallback((inspection: GeckoInspection) => {
    const date = dayjs(inspection.data.date).format('YYYY-MM-DD')
    const type = inspection.data.inspection_type || 'Unknown'
    return `${date} (${type})`
  }, [])

  // Inspection options for selectors
  const inspectionOptions = useMemo(() => {
    // Show all inspections, but indicate if they have CDFs
    return inspections.map((inspection) => {
      const hasCdf = inspectionCdfs.has(inspection.id)
      return {
        value: inspection.id,
        label: `${formatInspection(inspection)}${hasCdf ? '' : ' (no data)'}`,
        disabled: !hasCdf,
      }
    })
  }, [inspections, inspectionCdfs, formatInspection])

  // Calculate time difference between inspections
  const yearsDifference = useMemo(() => {
    if (!inspectionA || !inspectionB) return null
    const dateA = dayjs(inspectionA.data.date)
    const dateB = dayjs(inspectionB.data.date)
    const years = Math.abs(dateB.diff(dateA, 'year', true))
    return GeckoMeasurement.CreateFromValue(years, GeckoMeasurementUnit.Years)
  }, [inspectionA, inspectionB])

  // Calculate thickness loss
  const thicknessLoss = useMemo(() => {
    if (!selectedBinA || !selectedBinB) return null
    // A is older, B is newer, so loss = A - B (positive if material was lost)
    return selectedBinA.measurement.minus(selectedBinB.measurement)
  }, [selectedBinA, selectedBinB])

  // Calculate corrosion rate
  const calculatedCorrosionRate = useMemo(() => {
    if (!thicknessLoss || !yearsDifference || yearsDifference.num === 0)
      return null
    const rateValue =
      thicknessLoss.convertTo(GeckoMeasurementUnit.Millimeters).num /
      yearsDifference.num
    return GeckoMeasurement.CreateFromValue(
      rateValue,
      GeckoMeasurementUnit.MillimetersPerYear
    )
  }, [thicknessLoss, yearsDifference])

  // Validation
  const canConfirm = useMemo(() => {
    return (
      calculatedCorrosionRate !== null && calculatedCorrosionRate.num >= 0 // Don't allow negative corrosion rates
    )
  }, [calculatedCorrosionRate])

  const handleConfirm = useCallback(() => {
    if (calculatedCorrosionRate) {
      onConfirm(calculatedCorrosionRate)
      onClose()
    }
  }, [calculatedCorrosionRate, onConfirm, onClose])

  return (
    <Modal
      title='Short-Term Corrosion Rate Picker'
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose>
      <ModalContent>
        <Text type='secondary'>
          Select two inspections and click on the heatmaps to pick thickness
          values. The corrosion rate will be calculated from the thickness
          difference over time.
        </Text>

        {inspections.length < 2 && !isLoading && (
          <Alert
            type='warning'
            message='Need at least 2 inspections to calculate short-term corrosion rate'
            showIcon
          />
        )}

        {/* Inspection A selector (older) */}
        <InspectionSelectRow>
          <InspectionSelectLabel strong>
            Inspection A (older):
          </InspectionSelectLabel>
          <Select
            style={{ flex: 1 }}
            placeholder='Select older inspection'
            value={inspectionAId}
            onChange={setInspectionAId}
            options={inspectionOptions}
            loading={isLoading}
          />
        </InspectionSelectRow>

        {/* Inspection B selector (newer) */}
        <InspectionSelectRow>
          <InspectionSelectLabel strong>
            Inspection B (newer):
          </InspectionSelectLabel>
          <Select
            style={{ flex: 1 }}
            placeholder='Select newer inspection'
            value={inspectionBId}
            onChange={setInspectionBId}
            options={inspectionOptions}
            loading={isLoading}
          />
        </InspectionSelectRow>

        <Divider />

        {/* Heatmaps side by side */}
        <HeatmapsContainer>
          <InspectionHeatmapPicker
            cdf={cdfA}
            region={
              boundarySelectionEnabled ? expandedRegion : indicationRegion
            }
            selectedBin={selectedBinA}
            onBinSelect={setSelectedBinA}
            title={
              inspectionA ? `${formatInspection(inspectionA)}` : 'Inspection A'
            }
            originalRegion={
              boundarySelectionEnabled ? indicationRegion : undefined
            }
            boundarySelectionMode={boundarySelectionEnabled}
          />
          <InspectionHeatmapPicker
            cdf={cdfB}
            region={
              boundarySelectionEnabled ? expandedRegion : indicationRegion
            }
            selectedBin={selectedBinB}
            onBinSelect={setSelectedBinB}
            title={
              inspectionB ? `${formatInspection(inspectionB)}` : 'Inspection B'
            }
            originalRegion={
              boundarySelectionEnabled ? indicationRegion : undefined
            }
            boundarySelectionMode={boundarySelectionEnabled}
          />
        </HeatmapsContainer>

        <Divider />

        {/* Results section */}
        <ResultsSection>
          <Title level={5} style={{ margin: 0, marginBottom: '0.5rem' }}>
            Calculated Values
          </Title>
          <FfsParameterField
            label='Time Difference'
            measurementType='time'
            value={yearsDifference}
          />
          <FfsParameterField
            label='Thickness A'
            measurementType='thickness'
            value={selectedBinA?.measurement ?? null}
          />
          <FfsParameterField
            label='Thickness B'
            measurementType='thickness'
            value={selectedBinB?.measurement ?? null}
          />
          <FfsParameterField
            label='Thickness Loss (A - B)'
            measurementType='thickness'
            value={thicknessLoss}
            isValid={thicknessLoss ? thicknessLoss.num >= 0 : undefined}
          />
          <Divider style={{ margin: '0.5rem 0' }} />
          <FfsParameterField
            label='Corrosion Rate'
            measurementType='rate'
            value={calculatedCorrosionRate}
            isValid={canConfirm ? true : undefined}
          />
          {thicknessLoss && thicknessLoss.num < 0 && (
            <Alert
              type='warning'
              message='Negative loss indicates thickness increased (unlikely). Please verify your selections.'
              showIcon
              style={{ marginTop: '0.5rem' }}
            />
          )}
        </ResultsSection>

        <ButtonRow>
          <CantileverButton onClick={onClose}>Cancel</CantileverButton>
          <CantileverButton
            primary
            onClick={handleConfirm}
            disabled={!canConfirm}>
            Confirm
          </CantileverButton>
        </ButtonRow>
      </ModalContent>
    </Modal>
  )
}
